import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetNotification } from '../../../context/Notification';
import envVars from '../../../constants/envVars';
import useRequest from '../../../hooks/useRequest';
import { LeadDetailsResponse, LeadNotePayload } from '../types';
import { LEAD_DETAILS } from './constants';

type DeleteResponse = {
    id: number;
    deleted_at: string;
    next_latest_note: LeadNotePayload | null;
    __silent?: boolean;
};

// HWM-22633 round 4: server-confirmed cache patch. The DELETE response
// carries `next_latest_note` (either the new most-recent non-deleted note
// or null), letting the StickyNotePanel + list-row badge update without a
// refetch. The caller-supplied `onSuccess` receives that payload so the
// list-row override can be applied locally too.
const useDeleteLeadNote = (
    onSuccess?: (data: { next_latest_note: LeadNotePayload | null }) => void
) => {
    const req = useRequest();
    const queryClient = useQueryClient();
    const setNotification = useSetNotification();

    const request = ({
        leadId,
        noteId,
        silent = false
    }: {
        leadId: string;
        noteId: number;
        silent?: boolean;
    }): Promise<DeleteResponse> => {
        return req({
            url: `${envVars.API_RTM_HOSTNAME}enterprise/leads/${leadId}/notes/${noteId}/`,
            method: 'DELETE'
        }).then((res) => {
            (res as DeleteResponse).__silent = silent;
            return res as DeleteResponse;
        });
    };

    return useMutation({
        mutationFn: request,
        onSuccess: (data, vars) => {
            queryClient.setQueryData<LeadDetailsResponse | undefined>(
                [LEAD_DETAILS, vars.leadId],
                (old) => (old ? { ...old, latest_note: data.next_latest_note } : old)
            );
            if (!data.__silent) {
                setNotification({ message: 'Note deleted', type: 'neutral' });
            }
            onSuccess && onSuccess({ next_latest_note: data.next_latest_note });
        },
        onError: (error: { errors: string[] }) => {
            return error;
        }
    });
};

export default useDeleteLeadNote;
